import type { ReactNode } from "react";

const cellColor: Record<string, string> = {
  Y: "bg-green-100 text-green-800",
  A: "bg-blue-100 text-blue-800",
  N: "bg-red-100 text-red-700",
  "N/A": "bg-gray-100 text-gray-500",
};

export function Badge({ value }: { value: string }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${cellColor[value] ?? "text-gray-400"}`}>
      {value}
    </span>
  );
}

export function ExternalLink({ href }: { href: string | null }) {
  if (!href) return <span className="text-gray-300">—</span>;
  const url = new URL(href);
  return <a href={href} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs">{url.hostname}</a>;
}

const legend = [
  { key: "Y", label: "Sourced Directly", color: "bg-green-100 text-green-800" },
  { key: "A", label: "Available via Partner", color: "bg-blue-100 text-blue-800" },
  { key: "N", label: "Not Available", color: "bg-red-100 text-red-700" },
  { key: "N/A", label: "Not Applicable", color: "bg-gray-100 text-gray-500" },
];

export function Legend() {
  return (
    <div className="mb-6">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 m-0">Legend</p>
      <div className="flex flex-wrap gap-2">
        {legend.map(({ key, label, color }) => (
          <span key={key} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${color}`}>
            <span className="font-bold">{key}</span>
            <span className="opacity-75">—</span>
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

type Row = {
  market: string;
  historical: string;
  delayed: string;
  realTime: string;
  level2: string;
  agreements?: string | null;
  priceList?: string | null;
};

export function CoverageTable({ rows, withAgreements = false }: { rows: Row[]; withAgreements?: boolean }): ReactNode {
  const headers = withAgreements
    ? ["Market", "Historical", "Delayed", "Real Time", "Level 2", "Exchange Agreements", "Price List"]
    : ["Market", "Historical", "Delayed", "Real Time", "Level 2"];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-gray-200">
            {headers.map(h => (
              <th key={h} className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ market, historical, delayed, realTime, level2, agreements, priceList }) => (
            <tr key={market} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-2 px-3 font-medium text-gray-800 whitespace-nowrap">{market}</td>
              <td className="py-2 px-3"><Badge value={historical}/></td>
              <td className="py-2 px-3"><Badge value={delayed}/></td>
              <td className="py-2 px-3"><Badge value={realTime}/></td>
              <td className="py-2 px-3"><Badge value={level2}/></td>
              {withAgreements ? (
                <>
                  <td className="py-2 px-3"><ExternalLink href={agreements ?? null}/></td>
                  <td className="py-2 px-3"><ExternalLink href={priceList ?? null}/></td>
                </>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
